import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import baseApi from 'shared/api/baseApi';
import { useAppDispatch, useAppSelector } from 'shared/hooks/redux';
import getReCaptchaToken from 'shared/utils/getReCaptchaToken';
import isPreviewHost from 'shared/utils/isPreviewHost';

import { useSignInUserMutation } from '../services/auth.api';
import { signInUser } from '../store/user.slice';
import { checkIfTokenIsValid } from '../utils/authStorage';
import { getSafeRedirectPath } from '../utils/redirects';

const RECAPTCHA_SCRIPT_ID = 'backoffice-recaptcha';

const LoginView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAppSelector((state) => state.user);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signIn, { isLoading, error }] = useSignInUserMutation();
  const redirectPath = getSafeRedirectPath(searchParams.get('redirect'));

  const siteKey = import.meta.env.VITE_RECAPTCHA_CLIENT_KEY || '';

  useEffect(() => {
    // Previews skip reCAPTCHA entirely, so loading the script there would only log a domain error
    // and leave a dangling badge.
    if (!siteKey || import.meta.env.NODE_ENV === 'test' || import.meta.env.MODE === 'test' || isPreviewHost()) {
      return undefined;
    }

    if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      document.body.appendChild(script);
    }

    return () => {
      document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
      document.querySelector('.grecaptcha-badge')?.remove();
    };
  }, [siteKey]);

  if (checkIfTokenIsValid(user)) {
    return <Navigate to={redirectPath} replace />;
  }

  const submitLogin = async () => {
    try {
      const recaptchaToken = await getReCaptchaToken();
      const signedInUser = await signIn({ email: email.trim(), password, recaptchaToken }).unwrap();
      dispatch(baseApi.util.resetApiState());
      dispatch(signInUser(signedInUser));
      void navigate(redirectPath, { replace: true });
    } catch {
      // RTK Query exposes the user-facing login error through the mutation state.
    }
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    void submitLogin();
  };

  return (
    <section className="flex w-full flex-col gap-5">
      <header className="flex flex-col gap-3 text-center">
        <h1 className="m-0 font-display text-2xl font-bold text-brand-navy">Iniciar Sesión</h1>
        <p className="m-0 font-body text-sm text-text-muted">Ingresa tus credenciales</p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <label htmlFor="email" className="flex flex-col gap-1.5 text-sm font-semibold text-text-default">
          Correo electrónico
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 rounded-lg border border-brand-sky-light bg-brand-light px-3 text-sm font-normal text-text-default focus:border-transparent focus:outline-none focus:ring-2 focus:ring-border-focus"
          />
        </label>

        <label htmlFor="password" className="flex flex-col gap-1.5 text-sm font-semibold text-text-default">
          Contraseña
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 rounded-lg border border-brand-sky-light bg-brand-light px-3 text-sm font-normal text-text-default focus:border-transparent focus:outline-none focus:ring-2 focus:ring-border-focus"
          />
        </label>

        {error && (
          <p className="text-sm text-error">
            {'status' in error && error.status === 401
              ? 'Email o contraseña incorrectos.'
              : 'Error al ingresar. Intenta de nuevo.'}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="h-[46px] cursor-pointer rounded-lg bg-action-brand-bg text-sm font-semibold text-text-inverse transition-colors hover:bg-action-brand-hover disabled:opacity-60"
        >
          {isLoading ? 'Ingresando...' : 'Ingresar'}
        </button>
      </form>
    </section>
  );
};

export default LoginView;
